<template>
  <div class="login-container">
    <!-- 背景装饰 -->
    <div class="background-decoration"></div>
    
    <!-- 登录卡片 -->
    <div class="login-card">
      <!-- 应用图标 -->
      <div class="app-icon">
        <div class="icon-inner"></div>
      </div>
      
      <!-- 标题 -->
      <h1 class="title">欢迎回来</h1>
      <p class="subtitle">请登录您的账号继续使用</p>
      
      <!-- 登录表单 -->
      <form @submit.prevent="handleLogin" class="login-form">
        <!-- 用户名输入框 -->
        <div class="form-group" :class="{ error: usernameError }">
          <label class="input-label">用户名</label>
          <div class="input-wrapper">
            <i class="icon-user"></i>
            <input 
              type="text" 
              v-model="username" 
              placeholder="请输入用户名" 
              @input="validateUsername"
              class="form-input"
            >
          </div>
          <p class="error-message" v-if="usernameError">{{ usernameErrorMessage }}</p>
        </div>
        
        <!-- 密码输入框 -->
        <div class="form-group" :class="{ error: passwordError }">
          <label class="input-label">密码</label>
          <div class="input-wrapper">
            <i class="icon-lock"></i>
            <input 
              :type="showPassword ? 'text' : 'password'" 
              v-model="password" 
              placeholder="请输入密码"
              @input="validatePassword"
              class="form-input"
            >
            <button 
              type="button" 
              class="toggle-password"
              @click="showPassword = !showPassword"
            >
              <i :class="showPassword ? 'icon-eye-off' : 'icon-eye'"></i>
            </button>
          </div>
          <p class="error-message" v-if="passwordError">{{ passwordErrorMessage }}</p>
        </div>
        
        <!-- 验证码输入框 - Base64格式图片 -->
        <div class="form-group" :class="{ error: codeError }">
          <label class="input-label">验证码</label>
          <div class="input-wrapper code-wrapper">
            <i class="icon-code"></i>
            <input 
              type="text" 
              v-model="code" 
              placeholder="请输入图中验证码" 
              maxlength="4"
              @input="validateCode"
              class="form-input"
              :disabled="isLoadingCaptcha"
			  style="width:50%;"
            >
            <!-- 验证码图片和刷新按钮 -->
            <div class="captcha-container">
              <!-- 加载状态 -->
              <div v-if="isLoadingCaptcha" class="captcha-loading">
                <i class="spinner-small"></i>
              </div>
              
              <!-- 验证码图片 (Base64格式) -->
              <img 
                v-else 
                :src="captchaImage" 
                alt="验证码" 
                class="captcha-image"
                @click="refreshCaptcha"
                :class="{ 'captcha-error': captchaError }"
              >
              
            </div>
          </div>
          <!-- 验证码错误信息 -->
          <p class="error-message" v-if="codeError">{{ codeErrorMessage }}</p>
          <p class="error-message" v-if="captchaError">验证码加载失败，请点击刷新重试</p>
        </div>
        
        <!-- 忘记密码 -->
        <div class="forgot-password">
          <a href="#" @click.prevent>忘记密码?</a>
        </div>
        
        <!-- 登录按钮 -->
        <button 
          type="submit" 
          class="login-button"
          :disabled="isSubmitting || isLoadingCaptcha"
        >
          <span v-if="!isSubmitting">登录</span>
          <span v-if="isSubmitting" class="loading">
            <i class="spinner"></i> 登录中...
          </span>
        </button>
        

      </form>
      

    </div>
    
    <!-- 底部版权信息 -->
    <div class="footer">
      <p>© 2023 您的公司名称 版权所有</p>
    </div>
  </div>
</template>

<script>
import * as Api from './api.js'
import { Toast } from 'vant';
export default {
  data() {
    return {
      // 表单数据
      username: '',
      password: '',
      code: '', // 验证码
      showPassword: false,
      isSubmitting: false,
      
      // 验证码相关
      captchaImage: '', // 存储Base64格式的验证码图片
      captchaKey: '', // 用于验证的验证码令牌
      isLoadingCaptcha: false, // 验证码加载状态
      captchaError: false, // 验证码加载错误
      
      // 验证状态
      usernameError: false,
      usernameErrorMessage: '',
      passwordError: false,
      passwordErrorMessage: '',
      codeError: false,
      codeErrorMessage: '',
      
      // 社交登录选项
      socialLogins: [
        { name: '微信登录', icon: 'icon-wechat' },
        { name: 'QQ登录', icon: 'icon-qq' },
        { name: '微博登录', icon: 'icon-weibo' }
      ]
    };
  },
  mounted() {
    // 页面加载时获取验证码
    this.refreshCaptcha();
  },
  methods: {
    // 验证用户名
    validateUsername() {
      const usernameReg = /^[a-zA-Z0-9_]{3,20}$/;
      
      if (!this.username) {
        this.usernameError = true;
        this.usernameErrorMessage = '请输入用户名';
      } else if (!usernameReg.test(this.username)) {
        this.usernameError = true;
        this.usernameErrorMessage = '用户名需3-20位，可包含字母、数字和下划线';
      } else {
        this.usernameError = false;
        this.usernameErrorMessage = '';
      }
      return !this.usernameError;
    },
    
    // 验证密码
    validatePassword() {
      if (!this.password) {
        this.passwordError = true;
        this.passwordErrorMessage = '请输入密码';
      } else if (this.password.length < 6) {
        this.passwordError = true;
        this.passwordErrorMessage = '密码长度不能少于6位';
      } else {
        this.passwordError = false;
        this.passwordErrorMessage = '';
      }
      return !this.passwordError;
    },
    
    // 验证验证码
    validateCode() {
      if (!this.code) {
        this.codeError = true;
        this.codeErrorMessage = '请输入验证码';
      } else {
        this.codeError = false;
        this.codeErrorMessage = '';
      }
      return !this.codeError;
    },
    
    // 刷新验证码 - 通过API获取Base64格式图片
    async refreshCaptcha() {
      // 重置状态
      this.isLoadingCaptcha = true;
      this.captchaError = false;
      this.code = '';
      this.validateCode();
	  
        Api.captcha().then(res=>{
		  this.captchaImage = res.data['image_base']
		  this.captchaKey = res.data.key
		  this.captchaError = false;
		  this.isLoadingCaptcha = false;
        })
      
    },
    
    // 处理登录
    async handleLogin() {
      // 验证表单
      const isUsernameValid = this.validateUsername();
      const isPasswordValid = this.validatePassword();
      const isCodeValid = this.validateCode();
      
      if (!isUsernameValid || !isPasswordValid || !isCodeValid || this.captchaError) {
        // 滚动到第一个错误的输入框
        const firstError = document.querySelector('.form-group.error');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
      
      // 模拟登录请求
      this.isSubmitting = true;
	  
	  let formdata = {
		  'captcha':this.code,
		  'captchaKey':this.captchaKey,
		  'password':this.password,
		  'username':this.username
	  }
	  console.log(formdata)
	  
	  Api.login(formdata).then(res=>{
		  
		  console.log(res)
		  if(res.code==2000){
			  Toast({
			    message: res.msg,
			    icon: 'success',
			  });
		  }else{
			  Toast({
			    message: res.msg,
			    icon: 'error',
			  });
		  }
		  
		  console.log(res.data)
		
		  
		  
	  })
	  
	  
      
      // try {
      //   // 实际应用中替换为你的登录API地址
      //   const response = await fetch('/api/login', {
      //     method: 'POST',
      //     headers: {
      //       'Content-Type': 'application/json'
      //     },
      //     body: JSON.stringify({
      //       username: this.username,
      //       password: this.password,
      //       code: this.code,
      //       captchaToken: this.captchaToken // 提交验证码令牌用于验证
      //     })
      //   });
        
      //   const result = await response.json();
        
      //   if (response.ok && result.success) {
      //     alert('登录成功！');
      //     // 登录成功后通常需要刷新验证码
      //     this.refreshCaptcha();
      //     // 实际应用中这里应该是路由跳转
      //     // this.$router.push('/home');
      //   } else {
      //     // 登录失败，可能是验证码错误
      //     if (result.error === 'captcha_error') {
      //       this.codeError = true;
      //       this.codeErrorMessage = '验证码不正确';
      //       // 验证码错误后自动刷新
      //       setTimeout(() => this.refreshCaptcha(), 1000);
      //     } else {
      //       alert(result.message || '登录失败，请重试');
      //     }
      //   }
      // } catch (error) {
      //   console.error('登录请求失败:', error);
      //   alert('网络错误，请稍后重试');
      // } finally {
      //   this.isSubmitting = false;
      // }
    }
  }
};
</script>

<style scoped>
/* 基础样式和变量 */
:root {
  --primary-color: #3498db;
  --primary-dark: #2980b9;
  --text-color: #333;
  --text-light: #666;
  --border-color: #ddd;
  --error-color: #e74c3c;
  --background-color: #f5f7fa;
  --white: #fff;
  --radius: 8px;
  --shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
}

/* 页面容器 */
.login-container {
  min-height: 100vh;
  background-color: var(--background-color);
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

/* 背景装饰 */
.background-decoration {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 280px;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  border-radius: 0 0 50% 50% / 30%;
  z-index: 1;
}

/* 登录卡片 */
.login-card {
  width: 100%;
  max-width: 400px;
  background-color: var(--white);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  padding: 40px 25px;
  margin-top: 100px;
  position: relative;
  z-index: 2;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

/* 应用图标 */
.app-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: var(--white);
  box-shadow: var(--shadow);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: -80px auto 20px;
  position: relative;
  z-index: 3;
}

.icon-inner {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
}

/* 标题样式 */
.title {
  color: var(--text-color);
  font-size: 24px;
  text-align: center;
  margin-bottom: 8px;
  font-weight: 600;
}

.subtitle {
  color: var(--text-light);
  font-size: 14px;
  text-align: center;
  margin-bottom: 30px;
}

/* 表单样式 */
.login-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  margin-bottom: 8px;
  transition: all 0.3s ease;
}

.form-group.error .input-wrapper {
  border-color: var(--error-color);
}

.input-label {
  display: block;
  font-size: 14px;
  color: var(--text-color);
  margin-bottom: 6px;
  font-weight: 500;
}

.input-wrapper {
  position: relative;
  height: 48px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius);
  display: flex;
  align-items: center;
  padding: 0 15px;
  transition: border-color 0.3s ease;
}

/* 验证码输入框样式 */
.code-wrapper {
  padding-right: 10px;
}

/* 验证码图片容器 */
.captcha-container {
  display: flex;
  align-items: center;
  margin-left: 10px;
}

/* 验证码图片加载状态 */
.captcha-loading {
  width: 120px;
  height: 36px;
  border-radius: 4px;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 验证码图片 */
.captcha-image {
  width: 120px;
  height: 36px;
  border-radius: 4px;
  object-fit: cover;
  cursor: pointer;
  background-color: #f5f5f5;
  transition: all 0.2s ease;
}

/* 验证码错误状态 */
.captcha-error {
  border: 1px solid var(--error-color);
  background-color: rgba(231, 76, 60, 0.05);
}

/* 刷新验证码按钮 */
.refresh-captcha {
  background: none;
  border: none;
  color: var(--text-light);
  cursor: pointer;
  margin-left: 8px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.refresh-captcha:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.refresh-captcha:not(:disabled):hover {
  background-color: #f0f0f0;
  color: var(--primary-color);
}

.icon-refresh::before {
  content: "🔄";
  font-size: 16px;
}

.input-wrapper:focus-within {
  border-color: var(--primary-color);
  outline: none;
  box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
}

.form-input {
  flex: 1;
  height: 100%;
  border: none;
  outline: none;
  font-size: 16px;
  color: var(--text-color);
  background: transparent;
  padding-left: 8px;
}

.form-input:disabled {
  background-color: #f9f9f9;
  color: #999;
}

.form-input::placeholder {
  color: #ccc;
}

/* 图标样式 */
.icon-user::before {
  content: "👤";
  font-size: 18px;
  color: var(--text-light);
}

.icon-lock::before {
  content: "🔒";
  font-size: 18px;
  color: var(--text-light);
}

.icon-code::before {
  content: "🔑";
  font-size: 18px;
  color: var(--text-light);
}

.icon-eye::before {
  content: "👁️";
  font-size: 18px;
  color: var(--text-light);
}

.icon-eye-off::before {
  content: "🙈";
  font-size: 18px;
  color: var(--text-light);
}

.icon-wechat::before {
  content: "💬";
  font-size: 24px;
}

.icon-qq::before {
  content: "🐧";
  font-size: 24px;
}

.icon-weibo::before {
  content: "🔴";
  font-size: 24px;
}

/* 密码切换按钮 */
.toggle-password {
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 错误信息 */
.error-message {
  color: var(--error-color);
  font-size: 12px;
  margin-top: 4px;
  margin-left: 2px;
  min-height: 16px;
}

/* 忘记密码 */
.forgot-password {
  text-align: right;
  margin-bottom: 10px;
}

.forgot-password a {
  color: var(--primary-color);
  font-size: 14px;
  text-decoration: none;
  transition: color 0.2s ease;
}

.forgot-password a:hover {
  color: var(--primary-dark);
  text-decoration: underline;
}

/* 登录按钮 */
.login-button {
  height: 50px;
  background-color: var(--primary-color);
  color: var(--white);
  border: none;
  border-radius: var(--radius);
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.1s ease;
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.login-button:hover:not(:disabled) {
  background-color: var(--primary-dark);
}

.login-button:active:not(:disabled) {
  transform: scale(0.98);
}

.login-button:disabled {
  background-color: #b4d5f0;
  cursor: not-allowed;
}

/* 加载动画 */
.loading {
  display: flex;
  align-items: center;
  gap: 8px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  display: inline-block;
}

/* 小型加载动画 */
.spinner-small {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(100, 100, 100, 0.3);
  border-radius: 50%;
  border-top-color: var(--text-light);
  animation: spin 1s ease-in-out infinite;
  display: inline-block;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 注册选项 */
.register-option {
  text-align: center;
  margin-top: 20px;
  font-size: 14px;
  color: var(--text-light);
}

.register-option a {
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.register-option a:hover {
  color: var(--primary-dark);
  text-decoration: underline;
}

/* 其他登录方式 */
.other-login-methods {
  margin-top: auto;
  padding-top: 30px;
}

.divider {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.divider::before,
.divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background-color: var(--border-color);
}

.divider span {
  padding: 0 15px;
  font-size: 13px;
  color: var(--text-light);
}

.social-icons {
  display: flex;
  justify-content: center;
  gap: 30px;
}

.social-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #f5f5f5;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.social-icon:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

/* 底部版权 */
.footer {
  margin-top: 30px;
  margin-bottom: 20px;
  color: var(--text-light);
  font-size: 12px;
  text-align: center;
  position: relative;
  z-index: 2;
}

/* 响应式调整 */
@media (max-width: 375px) {
  .login-card {
    padding: 30px 20px;
    margin-top: 80px;
  }
  
  .app-icon {
    width: 70px;
    height: 70px;
  }
  
  .icon-inner {
    width: 35px;
    height: 35px;
  }
  
  .social-icons {
    gap: 20px;
  }
  
  .social-icon {
    width: 45px;
    height: 45px;
  }
  
  .captcha-image, .captcha-loading {
    width: 100px;
  }
}
</style>


// WEBPACK FOOTER //
// src/views/login/index.vue